import { Combobox, Group, Text, TextInput, useCombobox, type TextInputProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useState } from 'react';
import { useProjectPicker } from '@/api/catalog';
import type { Schemas } from '@/api/client';
import { useSuggestions } from '@/api/timeEntries';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { ProjectDot } from '@/components/ProjectDot';

type Suggestion = Schemas['Suggestion'];

interface Props extends Omit<TextInputProps, 'value' | 'onChange' | 'onSelect'> {
  value: string;
  onChange: (value: string) => void;
  onSelectSuggestion?: (s: Suggestion) => void;
  onEnter?: () => void;
}

/** Beschreibungsfeld mit Vorschlägen aus früheren Einträgen (TT-03). */
export function DescriptionInput({ value, onChange, onSelectSuggestion, onEnter, ...rest }: Props) {
  const { workspaceId } = useCurrentWorkspace();
  const [focused, setFocused] = useState(false);
  const [debounced] = useDebouncedValue(value, 200);
  const { data: suggestions = [] } = useSuggestions(workspaceId, debounced, focused && !!onSelectSuggestion);
  const { data: projects = [] } = useProjectPicker(workspaceId);
  const combobox = useCombobox();
  const visible = suggestions.filter((s) => s.description !== value || s.projectId);

  return (
    <Combobox
      store={combobox}
      withinPortal
      position="bottom-start"
      onOptionSubmit={(index) => {
        const s = visible[Number(index)];
        if (s) {
          onChange(s.description);
          onSelectSuggestion?.(s);
        }
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <TextInput
          value={value}
          onChange={(e) => {
            onChange(e.currentTarget.value);
            combobox.openDropdown();
          }}
          onFocus={() => {
            setFocused(true);
            combobox.openDropdown();
          }}
          onBlur={(e) => {
            setFocused(false);
            combobox.closeDropdown();
            rest.onBlur?.(e);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && combobox.getSelectedOptionIndex() < 0) {
              onEnter?.();
            }
          }}
          {...rest}
        />
      </Combobox.Target>
      {onSelectSuggestion && visible.length > 0 && (
        <Combobox.Dropdown miw={360}>
          <Combobox.Options>
            {visible.map((s, i) => {
              const p = projects.find((project) => project.id === s.projectId);
              const task = p?.tasks.find((tk) => tk.id === s.taskId);
              return (
                <Combobox.Option key={`${s.description}-${s.projectId}-${s.taskId}`} value={String(i)}>
                  <Group gap="xs" wrap="nowrap">
                    <Text size="sm" truncate>
                      {s.description}
                    </Text>
                    {p && <ProjectDot name={p.name} color={p.color} taskName={task?.name} size="xs" />}
                  </Group>
                </Combobox.Option>
              );
            })}
          </Combobox.Options>
        </Combobox.Dropdown>
      )}
    </Combobox>
  );
}
